import { Vendor } from "types/vendor";
import VendorDetailItem from "./VendorDetailItem/VendorDetailItem";
import { Button } from "components/button";
import styles from "./VendorDetails.module.scss";
import { useContext } from "react";
import { VendorContext } from "contexts/VendorContext";
import { useAreYouSure } from "components/areYouSure";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getVendorItemDetails } from "api/vendorsApi";
import { toast } from "react-toastify";
import axios from "axios";

type IProps = {
  vendor?: Vendor;
  refetch?: () => void | Promise<unknown>;
};

const VendorDetails = ({ vendor, refetch }: IProps) => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorBody.VendorDetails.VendorDetails";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { deleteVendor, openVendorEditModal } = useContext(VendorContext);
  const { activate } = useAreYouSure();

  const vendorItemsQuery = useQuery(
    ["vendors_items", vendor?.vendor_id],
    () => getVendorItemDetails(vendor?.vendor_id ?? null),
    { enabled: !!vendor?.vendor_id }
  );
  const vendorItemCount = vendorItemsQuery.data?.length ?? 0;

  const getDeleteBlockedMessage = () => {
    if (vendorItemCount === 1) {
      return tr("Remove the linked vendor item before deleting this vendor.");
    }
    return tr("Remove all vendor items before deleting this vendor.");
  };

  const getDeleteErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const apiMessage = error.response?.data?.error?.message;
      if (typeof apiMessage === "string" && apiMessage.length > 0) {
        return apiMessage;
      }
    }
    return tr("Error deleting vendor");
  };

  const handleDeleteVendor = () => {
    if (vendorItemCount > 0) {
      toast.error(getDeleteBlockedMessage());
      return;
    }

    activate(
      () => {
        deleteVendor(refetch).catch((error) => {
          toast.error(getDeleteErrorMessage(error));
        });
      },
      {
        message: tr("Are you sure you want to delete this vendor?"),
      }
    );
  };

  if (!vendor) return null;

  const adjustheight = () => {
    const textarea = document.getElementById("vendorTextArea");
    const container = document.getElementById("vendorDetailContainer");
    if (textarea && container) container.style.height = textarea.scrollHeight + "px";
  };

  return (
    <div className={styles.container}>
      <div className={styles.details}>
        <div className={styles.title}>Details</div>
        <VendorDetailItem label="ID" vendorKey={"vendor_id"} />
        <VendorDetailItem label={tr("Address")} vendorKey={"street_address"} />
        <VendorDetailItem label={tr("City")} vendorKey={"city"} />
        <VendorDetailItem label={tr("State")} vendorKey={"vendor_state"} limit={2} />
        <VendorDetailItem label={tr("Zip")} vendorKey={"zip_code"} limit={5} />
        <VendorDetailItem
          label={tr("Notes")}
          vendorKey={"notes"}
          isNotes
          adjustheight={adjustheight}
        />
        <Button
          size="small"
          onClick={() => openVendorEditModal()}
          style={{
            position: "absolute",
            bottom: "0",
            right: "0",
            margin: "10px",
            width: "8em",
          }}
        >
          {tr("Update")}
        </Button>
      </div>

      <div className={styles.contact}>
        <div className={styles.title}>{tr("Contact")}</div>
        <VendorDetailItem label={tr("First")} vendorKey={"first_name"} />
        <VendorDetailItem label={tr("Last")} vendorKey={"last_name"} />
        <VendorDetailItem label={tr("Phone")} vendorKey={"phone_number"} />
        <VendorDetailItem label={tr("Email")} vendorKey={"email_address"} />
        <Button
          size="small"
          onClick={handleDeleteVendor}
          style={{
            position: "absolute",
            bottom: "0",
            right: "0",
            margin: "10px",
          }}
        >
          {tr("Delete Vendor")}
        </Button>
      </div>
    </div>
  );
};

export default VendorDetails;
